import type { ReactNode } from 'react';

import { useTheme, type ThemePreference } from '@/app/ThemeProvider';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  PageHeader,
  SegmentedControl,
  Skeleton,
  StatusView,
} from '@/components/ui';
import { appwriteConfig } from '@/config/appwrite';
import { useAuth } from '@/features/auth/AuthProvider';
import { computeSessionStats, useSessions } from '@/features/sessions/hooks';
import { formatDuration, formatSessionDateTime } from '@/lib/formatting';
import { useProfile } from '../hooks';
import { formatBirthDate } from '../model';

import styles from './ProfilePage.module.css';

const themeOptions = [
  { value: 'dark' as const, label: 'Dark', icon: 'moon' as const },
  { value: 'light' as const, label: 'Light', icon: 'sun' as const },
  { value: 'system' as const, label: 'System', icon: 'settings' as const },
];

export function ProfilePage() {
  const { user, signOut, signingOut } = useAuth();
  const { profile, name, isPending, isError, refetch } = useProfile();
  const { sessions } = useSessions();
  const { preference, setPreference } = useTheme();

  const stats = computeSessionStats(sessions);
  const email = profile?.email ?? user?.email ?? null;

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="Profile"
        description="Your ELA Lab account, as recorded in the ela app."
      />

      <div className={styles.grid}>
        <Card className={styles.full}>
          <div className={styles.identityCard}>
            {isPending ? (
              <Skeleton width={88} height={88} radius="50%" />
            ) : (
              <Avatar name={name} imageId={profile?.profileImageId} size="lg" />
            )}

            <div className={styles.identityText}>
              {isPending ? (
                <>
                  <Skeleton width="42%" height={24} />
                  <Skeleton width="30%" text />
                </>
              ) : (
                <>
                  <div className={styles.identityName}>{name}</div>
                  {email !== null && email !== name ? (
                    <div className={styles.identityEmail}>{email}</div>
                  ) : null}
                  <div className={styles.identityBadges}>
                    <Badge tone={user?.emailVerification ? 'success' : 'warning'} dot>
                      {user?.emailVerification ? 'Verified' : 'Unverified'}
                    </Badge>
                    {profile !== null ? (
                      <Badge tone="accent">Profile on file</Badge>
                    ) : (
                      <Badge>No profile record</Badge>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className={styles.readonlyNote}>
            <Icon name="lock" size={14} className={styles.readonlyIcon} />
            <span>
              ELA Lab is a read-only view of your account. To change your name, photo or contact
              details, edit them in the ela app — the change appears here on your next visit.
            </span>
          </div>
        </Card>

        <Card>
          <CardHeader title="Personal details" subtitle="From your ela profile" />
          <CardBody>
            {isError ? (
              <StatusView
                icon="cloud-off"
                tone="danger"
                compact
                title="Could not load your profile"
                message="Your account details below are still available."
                action={
                  <Button icon="refresh" onClick={() => void refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : isPending ? (
              <div className={styles.rows}>
                <Skeleton text width="70%" />
                <Skeleton text width="55%" />
                <Skeleton text width="62%" />
              </div>
            ) : profile === null ? (
              <StatusView
                icon="user"
                compact
                title="No profile record yet"
                message="Your account has no profile row in the ela app. Filling one in there will populate this card."
              />
            ) : (
              <div className={styles.rows}>
                <Row label="Name" value={profile.name} />
                <Row label="Email" value={profile.email} />
                <Row label="Phone" value={profile.phoneNumber} />
                <Row label="Birth date" value={formatBirthDate(profile.birthDate)} />
                {profile.updatedAt !== null ? (
                  <Row label="Last updated" value={formatSessionDateTime(profile.updatedAt)} />
                ) : null}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Account" subtitle="From your Appwrite user record" />
          <CardBody>
            <div className={styles.rows}>
              <Row label="Account name" value={user?.name?.trim() ? user.name : null} />
              <Row label="Sign-in email" value={user?.email ?? null} />
              <Row label="User ID" value={user?.$id ?? null} mono />
              {profile !== null ? (
                <Row label="Profile ID" value={profile.id} mono />
              ) : null}
              {user?.registration ? (
                <Row label="Member since" value={formatSessionDateTime(new Date(user.registration))} />
              ) : null}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Your data" subtitle="Computed from your own session records" />
          <CardBody>
            <div className={styles.rows}>
              <Row label="Sessions" value={String(stats.total)} />
              <Row label="Time practised" value={formatDuration(stats.totalDurationSec)} />
              <Row label="Raw EEG files" value={String(stats.withRawData)} />
              <Row
                label="First session"
                value={stats.firstSession ? formatSessionDateTime(stats.firstSession) : null}
              />
              <Row
                label="Latest session"
                value={stats.lastSession ? formatSessionDateTime(stats.lastSession) : null}
              />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Appearance" />
          <CardBody>
            <div className={styles.themeRow}>
              <p className={styles.themeText}>
                ELA Lab is dark by default. Both themes use their own validated colour steps rather
                than an inverted copy of the other.
              </p>
              <SegmentedControl
                label="Theme"
                value={preference}
                options={themeOptions}
                onChange={(value) => setPreference(value as ThemePreference)}
              />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Connection" subtitle="The backend shared with the ela app" />
          <CardBody>
            <div className={styles.rows}>
              <Row label="Endpoint" value={appwriteConfig.endpoint} mono />
              <Row label="Project" value={appwriteConfig.projectId} mono />
              <Row label="Database" value={appwriteConfig.databaseId} mono />
              <Row label="Sessions" value={appwriteConfig.sessionSummariesCollectionId} mono />
              <Row label="Profiles" value={appwriteConfig.profilesCollectionId} mono />
              <Row label="EEG bucket" value={appwriteConfig.eegBucketId} mono />
              <Row label="Images bucket" value={appwriteConfig.profileImagesBucketId} mono />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Session" />
          <CardBody>
            <div className={styles.danger}>
              <p className={styles.themeText}>
                Signing out ends this browser's session only. Your other devices stay signed in.
              </p>
              <Button variant="danger" icon="logout" busy={signingOut} onClick={() => void signOut()}>
                Sign out
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

/** A label/value pair. `null` renders as an explicit "Not provided". */
function Row({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: ReactNode | null;
  mono?: boolean;
}) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      {value === null || value === undefined || value === '' ? (
        <span className={styles.absent}>Not provided</span>
      ) : (
        <span className={mono ? styles.mono : styles.value}>{value}</span>
      )}
    </div>
  );
}
